"use client";
import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { fns } from "cy-util";

export type RouteState = Record<string, string | undefined>;

function isClientSide() {
  return typeof window !== "undefined";
}
/**
 * NextJS Router hook to store state info in the url.
 */
export function useRouteState<T extends RouteState = RouteState>(
  defaultState: T | undefined = undefined,
) {
  const router = useRouter();
  const urlRef = useRef<URL | null>(null);
  const externalKeys = useRef<Record<string, boolean> | undefined>(undefined);
  // There are some route parameters that appear in router.query that are not part of state.
  // we dont want them in our router state and added again to as query strings.

  if (isClientSide() && externalKeys.current == null) {
    externalKeys.current = {};
    const url = new URL(window.location.href);
    for (const key of Object.keys(router.query)) {
      if (!url.searchParams.has(key)) {
        externalKeys.current[key] = true;
      }
    }
  }

  useEffect(() => {
    if (isClientSide()) {
      urlRef.current = new URL(window.location.href);
    }
  }, [router.asPath]);

  function setState(stateOrFn: T | ((state: T) => T)) {
    if (urlRef.current) {
      let state;
      if (typeof stateOrFn === "function") {
        state = stateOrFn(getState());
      } else {
        state = stateOrFn;
      }
      for (const key of Object.keys(state)) {
        const value = state[key];
        if (value == null) {
          urlRef.current.searchParams.delete(key);
        } else {
          urlRef.current.searchParams.set(key, value);
        }
      }

      const newUrl = urlRef.current.pathname + urlRef.current.search;
      router.push(newUrl, undefined, { shallow: true });
    }
  }

  function getState(): T {
    const state: Record<string, string | undefined> = {};
    if (defaultState) {
      for (const key of Object.keys(defaultState)) {
        state[key] = getValue(key);
      }
    }
    for (const key of Object.keys(router.query)) {
      if (!externalKeys.current || !externalKeys.current[key]) {
        state[key] = getValue(key);
      }
    }
    return state as T;
  }

  function getValue(key: string): string | undefined {
    const value = router.query[key];
    if (value === undefined && defaultState) return defaultState[key];
    if (typeof value === "string") {
      return value;
    } else if (Array.isArray(value)) {
      return value[0];
    }
    return undefined;
  }

  const state = getState();

  function useState(key: string, defaultValue?: string) {
    const _setState = (
      action:
        | string
        | undefined
        | ((val: string | undefined) => string | undefined),
    ) => {
      if (typeof action === "function") {
        const newState = { ...state, [key]: action(state[key]) };
        setState(newState);
      } else {
        setState((x) => ({ ...x, [key]: action }));
      }
    };

    const currentValue = fns.ifElse(
      (x) => x == null,
      fns.always(defaultValue),
      fns.identity,
      state[key],
    );
    return [currentValue, _setState] as const;
  }

  return { state, setState, useState } as const;
}
